import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Zap, Shield, BarChart3, MessageSquare, Truck, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { startCheckoutOrDetectExisting } from '@/lib/billing/changePlan';
import { ChangePlanDialog } from '@/components/billing/ChangePlanDialog';
import { purchasesEnabled, NATIVE_BILLING_NOTICE } from '@/lib/billing/purchaseGate';


const FEATURES = [
  { icon: Zap, title: 'AI-Powered Load Matching', desc: 'Get carriers matched to your loads in seconds based on price, capacity, and lane history.' },
  { icon: BarChart3, title: 'Real-Time Rate Engine', desc: 'Instantly see what loads are booking for right now, not last week.' },
  { icon: Shield, title: 'FMCSA-Verified Carrier Network', desc: 'Search and message only active, compliant carriers.' },
  { icon: Truck, title: 'Full Load Posting & Negotiation Tools', desc: 'Post loads, send offers, negotiate, and update pricing without leaving the dashboard.' },
  { icon: TrendingUp, title: 'AI Repricing & Lane Intelligence', desc: 'See underpriced loads, suggested new rates, and lane performance.' },
  { icon: MessageSquare, title: 'Unlimited Messaging + Offer Management', desc: 'Respond faster. Book faster. All inside LaneRunner.' },
];

/**
 * Wraps dashboard sections with a blur overlay when the user
 * does not have an active subscription.  A single floating
 * upgrade modal is rendered once (via a portal-style pattern)
 * rather than once per section.
 */

// ── Blur wrapper for individual sections ────────────────────
export const GatedSection: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { hasActiveSubscription, loading } = useAuth();

  if (loading || hasActiveSubscription) return <>{children}</>;

  return (
    <div className="relative">
      <div
        className="pointer-events-none select-none"
        style={{ filter: 'blur(2px)', opacity: 0.9 }}
        aria-hidden="true"
      >
        {children}
      </div>
    </div>
  );
};

// ── Non-dismissable upgrade overlay ─────────────────────────
export const UpgradeOverlay: React.FC = () => {
  const { profile, user, hasActiveSubscription, loading } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [changeOpen, setChangeOpen] = useState(false);

  if (loading || hasActiveSubscription) return null;

  const targetTier = profile?.user_type === 'carrier' ? 'carrier_full' : 'broker';

  const handleUpgrade = async () => {
    if (!user || !profile) return;
    setIsLoading(true);

    try {
      const outcome = await startCheckoutOrDetectExisting({
        email: profile.email,
        seats: 1,
        userId: user.id,
        userType: profile.user_type,
        fmcsaData: profile.fmcsa_legal_name ? {
          legalName: profile.fmcsa_legal_name,
          dotNumber: profile.fmcsa_dot_number,
          mcNumber: profile.fmcsa_mc_number,
        } : undefined,
        successUrl: `${window.location.origin}${window.location.pathname}?checkout=success&session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: window.location.href,
      });

      if (outcome.kind === 'redirect') {
        window.location.href = outcome.url;
        return;
      }
      if (outcome.kind === 'existing_subscription') {
        // Never open a second subscription — switch the existing one instead.
        setChangeOpen(true);
        return;
      }
      toast.error(outcome.message);
    } catch (err) {
      console.error('Checkout error:', err);
      toast.error('Could not start checkout. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
      {/* Semi-transparent scrim */}
      <div className="absolute inset-0 bg-background/10 pointer-events-auto" />

      {/* Modal card */}
      <div className="relative pointer-events-auto w-full max-w-2xl mx-4 my-3 rounded-2xl border bg-card shadow-2xl p-6 flex flex-col items-center text-center gap-4 animate-in fade-in zoom-in-95 duration-300">
        {/* Accent glow */}
        <div className="absolute -top-px left-1/2 -translate-x-1/2 w-3/4 h-px bg-gradient-to-r from-transparent via-accent to-transparent" />

        <div className="w-14 h-14 rounded-xl bg-accent/10 border border-accent/20 flex items-center justify-center">
          <Zap className="w-7 h-7 text-accent" />
        </div>

        <div>
          <h2 className="text-xl font-bold text-foreground mb-1">Unlock the full LaneRunner command center.</h2>
          <p className="text-sm text-muted-foreground">
            {purchasesEnabled()
              ? 'Start your free trial and post, quote, and move freight instantly.'
              : 'Your account does not currently include full access.'}
          </p>
        </div>

        <p className="text-sm font-medium text-foreground/90 w-full text-left">You'll get full access to:</p>

        <div className="w-full grid grid-cols-2 gap-3 text-left">
          {FEATURES.map(({ icon: Icon, title, desc }) => (
            <div key={title} className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center shrink-0 mt-0.5">
                <Icon className="w-4 h-4 text-accent" />
              </div>
              <div>
                <span className="text-sm font-semibold text-foreground">{title}</span>
                <p className="text-xs text-muted-foreground leading-relaxed">{desc}</p>
              </div>
            </div>
          ))}
        </div>

        <p className="text-sm font-bold text-foreground text-center">
          No limits. No restrictions.<br />
          Run your entire operation from one dashboard.
        </p>

        <div className="w-full pt-1">
          {!purchasesEnabled() ? (
            <p className="text-xs text-muted-foreground text-center">
              {NATIVE_BILLING_NOTICE}
            </p>
          ) : (
          <>
          <Button
            onClick={handleUpgrade}
            disabled={isLoading}
            className="w-full h-11 text-sm font-semibold"
            size="lg"
          >
            {isLoading ? 'Redirecting…' : 'Start your FREE trial →'}
          </Button>
          <p className="text-[11px] text-muted-foreground text-center mt-2">
            Credit card required for fraud prevention. Cancel anytime.
          </p>
          <p className="text-xs text-muted-foreground/70 text-center mt-1">
            Move your first load today.
          </p>
          </>
          )}
        </div>
      </div>

      <ChangePlanDialog
        open={changeOpen}
        onOpenChange={setChangeOpen}
        tier={targetTier}
        tierLabel="Full Access — $99.99/month per user"
      />
    </div>

  );
};
